import React, { useEffect, useState } from 'react';
import apiService from '../../services/ApiService';
import { useToast } from '../../contexts/ToastContext';
import ImageCropModal from '../ImageCropModal';
import { readFileAsDataURL } from '../../utils/cropImage';
import { AdminTool, ToolIcon, hasBrandIcon } from '../tools-catalog/catalogModel';

interface ToolImage { id: number; toolId: number; role: string; width: number; height: number; fileSizeBytes: number; uploadedAt: string }

// Ruoli usati dai frontend: tab = icona del tool, logo = logo orizzontale del launcher.
const IMAGE_ROLES = [
    { role: 'tab', label: 'Icona', hint: 'Quadrata, 256 × 256', aspect: 1, w: 256, h: 256 },
    { role: 'logo', label: 'Logo orizzontale', hint: 'Proporzione 15:4, 720 × 192', aspect: 15 / 4, w: 720, h: 192 },
] as const;
type RoleConfig = typeof IMAGE_ROLES[number];

/** Icona e logo: anteprima alle dimensioni in cui compaiono, caricamento e rimozione. */
const LookSection: React.FC<{ tool: AdminTool; onImagesChanged: (roles: string[]) => void }> = ({ tool, onImagesChanged }) => {
    const { showToast } = useToast();
    const [images, setImages] = useState<ToolImage[]>([]);
    const [version, setVersion] = useState(1);
    const [crop, setCrop] = useState<{ cfg: RoleConfig; src: string } | null>(null);
    const brand = hasBrandIcon(tool.code);

    const reload = async () => {
        const list = await apiService.adminGetToolImages<ToolImage>(tool.id);
        setImages(list);
        setVersion(v => v + 1);
        onImagesChanged(list.map(i => i.role));
    };
    useEffect(() => {
        apiService.adminGetToolImages<ToolImage>(tool.id).then(setImages).catch(() => setImages([]));
    }, [tool.id]);

    const upload = async (cfg: RoleConfig, file: Blob, w: number, h: number) => {
        try {
            await apiService.adminUploadToolImage(tool.id, cfg.role, file, w, h);
            await reload();
            showToast('Immagine salvata', cfg.label, 'success');
        } catch {
            showToast('Immagine non salvata', cfg.label, 'danger');
        }
    };

    const pick = async (e: React.ChangeEvent<HTMLInputElement>, cfg: RoleConfig) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        // Gli SVG si salvano tali e quali, senza ritaglio.
        if (file.type === 'image/svg+xml' || file.name.toLowerCase().endsWith('.svg')) {
            const text = await file.text().catch(() => '');
            const m = text.match(/viewBox\s*=\s*"[\d.]+\s+[\d.]+\s+([\d.]+)\s+([\d.]+)"/i);
            await upload(cfg, file, m ? Math.round(Number(m[1])) : cfg.w, m ? Math.round(Number(m[2])) : cfg.h);
            return;
        }
        setCrop({ cfg, src: await readFileAsDataURL(file) });
    };

    const remove = async (cfg: RoleConfig) => {
        if (!window.confirm(`Eliminare ${cfg.label.toLowerCase()} caricato?`)) return;
        await apiService.adminDeleteToolImage(tool.id, cfg.role);
        await reload();
    };

    return (
        <div className="td-look">
            <section className="td-card" aria-labelledby="td-sizes-title">
                <h2 id="td-sizes-title" className="td-card__title">Come si vede</h2>
                <p className="td-card__lead">
                    {brand
                        ? 'Il portale include già un’icona per questo tool: è quella che vedono gli utenti. Un’immagine caricata qui la sostituisce solo nelle viste che leggono le immagini dell’admin.'
                        : 'Senza icona il catalogo mostra le iniziali del tool.'}
                </p>
                <div className="td-sizes">
                    {[64, 44, 36, 24].map(size => (
                        <figure key={size} className="td-sizes__item">
                            <ToolIcon tool={tool} size={size} version={version} />
                            <figcaption>{size === 64 ? 'Intestazione' : size === 44 ? 'Card del catalogo' : size === 36 ? 'Elenchi' : 'Menu'} · {size} px</figcaption>
                        </figure>
                    ))}
                </div>
            </section>

            <div className="td-look__grid">
                {IMAGE_ROLES.map(cfg => {
                    const image = images.find(i => i.role === cfg.role);
                    return (
                        <section key={cfg.role} className="td-card" aria-labelledby={`td-img-${cfg.role}`}>
                            <h2 id={`td-img-${cfg.role}`} className="td-card__title">{cfg.label}</h2>
                            <p className="td-card__lead">{cfg.hint}. PNG o JPG vengono ritagliati; un SVG si salva com’è.</p>
                            <div className={`td-look__frame td-look__frame--${cfg.role}`}>
                                {image
                                    ? <img src={`${apiService.toolImageUrl(tool.code, cfg.role)}?v=${version}`} alt={`${cfg.label} di ${tool.name}`} />
                                    : <span>Nessuna immagine caricata</span>}
                            </div>
                            <div className="td-look__actions">
                                <label className="btn btn-outline-primary btn-sm mb-0">
                                    <i className="bi bi-upload me-1" aria-hidden="true" />{image ? 'Sostituisci' : 'Carica'}
                                    <input type="file" accept="image/*,.svg,image/svg+xml" hidden onChange={e => void pick(e, cfg)} />
                                </label>
                                {image && <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => void remove(cfg)}>Elimina</button>}
                            </div>
                        </section>
                    );
                })}
            </div>

            {crop && (
                <ImageCropModal
                    imageSrc={crop.src}
                    aspect={crop.cfg.aspect}
                    outputWidth={crop.cfg.w}
                    outputHeight={crop.cfg.h}
                    title={`Ritaglia — ${crop.cfg.label}`}
                    onCancel={() => setCrop(null)}
                    onConfirm={async blob => { const cfg = crop.cfg; setCrop(null); await upload(cfg, blob, cfg.w, cfg.h); }}
                />
            )}
        </div>
    );
};

export default LookSection;
